/*
 * Copyright 2026 Sony Corporation
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  reload: () => void;
}

interface State<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
}

/** Last results by key, for the life of the tab. */
const remembered = new Map<string, unknown>();

/**
 * Runs an async read and keeps the last result.
 *
 * The generation guard is the point: a slow request that resolves after the
 * component moved on (a different world, an unmount) must not write its result.
 * Nothing is set synchronously inside the effect, so starting a load cannot
 * cascade into a second render pass before the request is even in flight.
 *
 * With a `cacheKey`, the last result under that key is shown at once and then
 * refreshed, so returning to a page does not start from a spinner.
 */
export const useAsync = <T>(load: () => Promise<T>, deps: unknown[], cacheKey?: string): AsyncState<T> => {
  const [state, setState] = useState<State<T>>(() => ({
    data: cacheKey === undefined ? undefined : (remembered.get(cacheKey) as T | undefined),
    error: undefined,
    loading: true,
  }));
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;

    load()
      .then((data) => {
        if (current === generation.current) {
          if (cacheKey !== undefined) {
            remembered.set(cacheKey, data);
          }
          setState({ data, error: undefined, loading: false });
        }
      })
      .catch((error: Error) => {
        if (current === generation.current) {
          setState((previous) => ({ ...previous, error, loading: false }));
        }
      });

    return () => {
      generation.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => {
    setState((previous) => ({ ...previous, loading: true }));
    setNonce((value) => value + 1);
  }, []);

  return { ...state, reload };
};
